import { useState } from 'react';
import { Button } from 'system/atoms';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { modalV2Atom } from 'common/modal/stores/atom';
import { useRecoilValue } from 'recoil';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import { ModalV2Inputs } from 'common/modal/types/modalV2';
import { Form } from 'common/form';
import { modalV2Sx } from '../styles';

const ModalV2Content: React.FC<{
  inputs: ModalV2Inputs;
}> = ({
  inputs: {
    title,
    subtitle,
    form,
    onPrimaryAction,
    onSecondaryAction,
    primaryButtonText = 'Confirm',
    secondaryButtonText = 'Cancel',
    primaryButtonProps = {},
    secondaryButtonProps = {},
    tertiaryButtonText,
    onTertiaryAction,
    stackActions = false,
    afterActionsChildren
  }
}) => {
  const { closeModal } = useModalV2();
  const [isPrimaryActionLoading, setIsPrimaryActionLoading] = useState(false);
  const [isSecondaryActionLoading, setIsSecondaryActionLoading] =
    useState(false);
  const [isTertiaryActionLoading, setIsTertiaryActionLoading] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePrimaryAction = async (data?: any) => {
    if (onPrimaryAction) {
      setIsPrimaryActionLoading(true);
      try {
        // An action reporting failure keeps the modal open, so the user can retry without
        // reopening the flow that got them here.
        if ((await onPrimaryAction(data)) === false) {
          return;
        }
      } finally {
        setIsPrimaryActionLoading(false);
      }
    }
    closeModal();
  };

  const handleTertiaryAction = async () => {
    if (onTertiaryAction) {
      setIsTertiaryActionLoading(true);
      try {
        if ((await onTertiaryAction()) === false) {
          return;
        }
      } finally {
        setIsTertiaryActionLoading(false);
      }
    }
    closeModal();
  };

  const handleSecondaryAction = async () => {
    if (onSecondaryAction) {
      setIsSecondaryActionLoading(true);
      try {
        await onSecondaryAction();
      } finally {
        setIsSecondaryActionLoading(false);
      }
    }
    closeModal();
  };

  return (
    <>
      {title && <DialogTitle sx={modalV2Sx.dialogTitle}>{title}</DialogTitle>}
      <DialogContent sx={modalV2Sx.getDialogContentSx(!!title)}>
        <Stack gap="20px">
          {subtitle && (
            <Typography sx={modalV2Sx.subtitleText}>{subtitle}</Typography>
          )}
          {form && (
            <Stack>
              <Form
                {...form}
                submitButtonProps={{
                  label: primaryButtonText,
                  sx: modalV2Sx.submitButton,
                  loading: isPrimaryActionLoading || isSecondaryActionLoading,
                  loadingType: 'bar',
                  ...(form.submitButtonProps ?? {}),
                  ...primaryButtonProps
                }}
                onSubmit={handlePrimaryAction}
                onCancel={
                  secondaryButtonProps.hidden
                    ? undefined
                    : handleSecondaryAction
                }
                cancelButtonProps={{
                  sx: modalV2Sx.getCancelButtonSx(
                    isPrimaryActionLoading || isSecondaryActionLoading
                  ),
                  disabled: isPrimaryActionLoading || isSecondaryActionLoading,
                  ...secondaryButtonProps
                }}
              />
            </Stack>
          )}
          {!form && (
            <Stack
              direction={stackActions ? 'column' : 'row'}
              gap={stackActions ? '10px' : '16px'}
              justifyContent="space-between"
            >
              <Button
                variant="primary"
                fullWidth
                onClick={() => handlePrimaryAction()}
                loading={isPrimaryActionLoading}
                disabled={isSecondaryActionLoading || isTertiaryActionLoading}
                {...primaryButtonProps}
              >
                {primaryButtonText}
              </Button>
              {tertiaryButtonText && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={handleTertiaryAction}
                  loading={isTertiaryActionLoading}
                  disabled={isPrimaryActionLoading || isSecondaryActionLoading}
                >
                  {tertiaryButtonText}
                </Button>
              )}
              {!secondaryButtonProps.hidden && (
                <Button
                  variant="tertiary"
                  fullWidth
                  onClick={handleSecondaryAction}
                  loading={isSecondaryActionLoading}
                  disabled={isPrimaryActionLoading || isTertiaryActionLoading}
                  {...secondaryButtonProps}
                >
                  {secondaryButtonText}
                </Button>
              )}
            </Stack>
          )}
          {afterActionsChildren && afterActionsChildren}
        </Stack>
      </DialogContent>
    </>
  );
};

export const ModalV2 = () => {
  const modalV2 = useRecoilValue(modalV2Atom);
  const { closeModal } = useModalV2();

  const { isOpen, inputs } = modalV2;

  return (
    <Dialog
      open={isOpen}
      onClose={() => closeModal()}
      sx={modalV2Sx.dialog}
      PaperProps={{
        sx: modalV2Sx.getDialogPaperSx(inputs?.width)
      }}
      fullWidth
    >
      {inputs && <ModalV2Content inputs={inputs} />}
    </Dialog>
  );
};
